import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { AuthContext } from '../context/AuthContext';
import { MapPin, DollarSign, Briefcase, Calendar, AlertCircle } from 'lucide-react';
import ScreeningQuestions from '../components/ScreeningQuestions';

export default function JobDetail() {
  const { id } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cvUrl, setCvUrl] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [screeningResponses, setScreeningResponses] = useState([]);
  const [showScreening, setShowScreening] = useState(false);

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      try {
        const response = await api.get(`/api/jobs/${id}`);
        setJob(response.data);
      } catch (err) {
        console.error('Failed to load job details:', err);
        // Fallback sample job when backend is unavailable
        setJob({
          id: 'sample-1',
          title: 'Frontend Engineer (React)',
          employer_name: 'Acme Tech',
          location: 'Harare, Zimbabwe',
          salary_min: 800,
          salary_max: 1500,
          experience_level: 'Mid Level',
          description: 'Build beautiful, performant user interfaces and ship delightful user experiences.',
          requirements: JSON.stringify(['3+ years React', 'Good CSS/Tailwind', 'Familiar with REST APIs']),
          responsibilities: 'Design, develop, and maintain UI components. Work closely with designers and backend engineers.',
          benefits: ['Health insurance', 'Flexible hours', 'Remote options']
        });
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id]);

  const handleApply = async (e) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.user_type !== 'job_seeker') {
      setError('Only job seekers can apply for jobs');
      return;
    }

    // Validate that user has provided at least one detail
    if (!cvUrl && !cvFile && !coverLetter.trim()) {
      setError('Please provide at least a CV link, upload a CV file, or write a cover letter');
      return;
    }

    setApplying(true);
    setError('');

    try {
      let applicationId;
      
      // If a file is selected, submit multipart/form-data
      if (cvFile) {
        const form = new FormData();
        form.append('job_id', id);
        form.append('cover_letter', coverLetter);
        form.append('cv', cvFile);

        const res = await api.post('/api/applications', form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        applicationId = res.data.id;
      } else {
        const res = await api.post('/api/applications', {
          job_id: id,
          cv_url: cvUrl,
          cover_letter: coverLetter
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        applicationId = res.data.id;
      }

      // Submit screening responses if any
      if (screeningResponses.length > 0 && applicationId) {
        await api.post('/api/screening-questions/responses', {
          application_id: applicationId,
          responses: screeningResponses
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setSuccess('Application submitted successfully!');
      setCvUrl('');
      setCvFile(null);
      setCoverLetter('');
      setScreeningResponses([]);
      setShowScreening(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500">Loading job details...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-500">Job not found</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">{job.title}</h1>
          <p className="text-xl text-gray-600 mb-6">{job.employer_name}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {job.location && (
              <div className="flex items-center space-x-2">
                <MapPin className="text-blue-600" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-semibold">{job.location}</p>
                </div>
              </div>
            )}
            {job.salary_min && (
              <div className="flex items-center space-x-2">
                <DollarSign className="text-green-600" size={20} />
                <div>
                  <p className="text-sm text-gray-500">Salary</p>
                  <p className="font-semibold">${job.salary_min} - ${job.salary_max}</p>
                </div>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Briefcase className="text-purple-600" size={20} />
              <div>
                <p className="text-sm text-gray-500">Level</p>
                <p className="font-semibold">{job.experience_level}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Calendar className="text-orange-600" size={20} />
              <div>
                <p className="text-sm text-gray-500">Posted</p>
                <p className="font-semibold">{new Date(job.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          <hr className="my-6" />

          <h2 className="text-2xl font-bold mb-4">About this role</h2>
          <p className="text-gray-700 whitespace-pre-wrap mb-6">{job.description}</p>

          {job.responsibilities && (
            <>
              <h2 className="text-2xl font-bold mb-4">Responsibilities</h2>
              <p className="text-gray-700 mb-6">{job.responsibilities}</p>
            </>
          )}

          {job.requirements && (
            <>
              <h2 className="text-2xl font-bold mb-4">Requirements</h2>
              <ul className="list-disc list-inside text-gray-700 mb-6">
                {JSON.parse(job.requirements || '[]').map((req, idx) => (
                  <li key={idx}>{req}</li>
                ))}
              </ul>
            </>
          )}

          {job.benefits && job.benefits.length > 0 && (
            <div className="bg-green-50 border border-green-100 rounded-lg p-6 mb-6">
              <h2 className="text-xl font-bold mb-2">Benefits</h2>
              <ul className="list-disc list-inside text-gray-700">
                {job.benefits.map((b, idx) => <li key={idx}>{b}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* Application Form */}
        {user && user.user_type === 'job_seeker' && (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6">Apply for this Job</h2>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start space-x-3">
                <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-green-800">{success}</p>
              </div>
            )}

            <form onSubmit={handleApply} className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Please provide at least one of the following: CV link, uploaded CV file, or cover letter.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CV Link <span className="text-xs text-gray-500">(Recommended)</span>
                </label>
                <input
                  type="url"
                  value={cvUrl}
                  onChange={(e) => setCvUrl(e.target.value)}
                  placeholder="Link to your CV (e.g., Google Drive, Dropbox)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
                <p className="text-xs text-gray-600 mt-2">
                  💡 <strong>Best option:</strong> Upload to Google Drive/Dropbox and paste the shareable link here
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or upload CV <span className="text-xs text-gray-500">(Optional)</span>
                </label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files[0] || null;
                    setCvFile(file);
                    if (file) setCvUrl(''); // Clear CV link when file is uploaded
                  }}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 mt-1">Accepted: PDF, DOC, DOCX. <span className="font-semibold text-amber-700">⚠️ Recommended: Use an external link (Google Drive, Dropbox) for better reliability.</span></p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cover Letter <span className="text-xs text-gray-500">(Optional)</span>
                </label>
                <textarea
                  value={coverLetter}
                  onChange={(e) => setCoverLetter(e.target.value)}
                  placeholder="Tell the employer why you're interested in this position..."
                  rows="6"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                />
              </div>

              {/* Screening Questions */}
              {!showScreening && (
                <button
                  type="button"
                  onClick={() => setShowScreening(true)}
                  className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-200 transition text-sm"
                >
                  View Screening Questions (if any)
                </button>
              )}

              {showScreening && (
                <ScreeningQuestions 
                  jobId={id} 
                  onSubmit={(responses) => setScreeningResponses(responses)}
                />
              )}

              <button
                type="submit"
                disabled={applying}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applying ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        )}

        {!user && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
            <p className="text-gray-700 mb-4">Please log in to apply for this job</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
            >
              Login
            </button>
          </div>
        )}

        {user && user.user_type === 'employer' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
            <p className="text-gray-700">Employers cannot apply for jobs. Switch to a job seeker account to apply.</p>
          </div>
        )}
      </div>
    </div>
  );
}
